import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { Box } from '@material-ui/core';

import Accordion from '../../../components/Accordion';
import DataTableMui from '../../../components/DataTableMui';
import DatepickerRange from '../../../components/Inputs/DatepickerRange';
import Icon from '../../../components/Icons';
import Input from '../../../components/Inputs/Input';
import Radio from '../../../components/Inputs/Radio';
import Select from '../../../components/Inputs/Select';
import Alert from '../../../components/Alert';

import { addMaskCpf } from '../../../utils/formatValues';
import { useGlobal } from '../../../contexts/Global/context';
import { getHistoricoMovimentacoes, dadosMovimentacaoHistorico } from '../../../services';
import validateForm from '../../../utils/validateForm';
import { translateContractsGroupsListToOptions } from '../../../utils/translateContractsGroupsListToOptions';

import { CustomStatusTag } from './CustomStatusTag';
import { changeRequiredOptionsFields, getPayload } from './configurationFieldsByType';
import schema from './schema';

import * as S from './styles';
import { cloneObject } from '../../../utils';

function HistoricoDeMovimentacao() {
    const [filterData, setFilterData] = useState(schema);
    const [movementHistoryData, setMovementHistoryData] = useState([]);

    const [isAlreadySearch, setIsAlreadySearch] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState({});

    const [showTableMessage, setShowTableMessage] = useState(false);

    const [rangeLimits, setRangeLimits] = useState({
        min: schema.period.value[0],
        max: schema.period.value[1],
    });

    const { contractsGroupsList, dataActiveContract, contrastTheme } = useGlobal();
    const listAllContracts = translateContractsGroupsListToOptions(contractsGroupsList);

    function handleChange(event) {
        const { id, value } = event;

        let newFilterData = { ...filterData, [id]: { ...filterData[id], value } };

        newFilterData = changeRequiredOptionsFields(newFilterData);

        setFilterData(newFilterData);
    }

    async function handleSearch() {
        const { isValid, validatedForm } = validateForm(filterData);

        setFilterData(validatedForm);

        if (!isValid) return;

        setIsAlreadySearch(false);
        setShowTableMessage(false);
        setIsLoading(true);

        const payload = getPayload(filterData, dataActiveContract.numero);
        const response = await getHistoricoMovimentacoes(payload);

        const formattedData =
            response.results?.map((data) => {
                const [date, hour] = data.dtOperacao.split(' ');

                const formattedDateHour = `${moment(date).format('DD/MM/YYYY')} às ${hour}`;

                return {
                    ...data,
                    dtOperacao: formattedDateHour,
                };
            }) || [];

        if (response.totalRecords > 100) setShowTableMessage(true);

        setMovementHistoryData(formattedData);

        setIsAlreadySearch(true);
        setIsLoading(false);
    }

    async function handleModal(row) {
        const response = await dadosMovimentacaoHistorico(row.numSequencialMov);

        setModalContent({ ...row, history: response });
        setShowModal(true);
    }

    function handleChangeRange(date, _, { range }) {
        if (!date) {
            setRangeLimits({
                min: '',
                max: '',
            });

            return;
        }

        const cloneDate = cloneObject(date);

        let newMax = moment(cloneDate[0]).add(90, 'days');

        if (newMax > moment()) newMax = moment();

        if (range === 'start') {
            setRangeLimits({
                ...rangeLimits,
                max: newMax,
            });
        }

        if (range === 'end') {
            setRangeLimits({
                ...rangeLimits,
                min: moment(cloneDate[1]).subtract(90, 'days'),
            });
        }
    }

    const columnsHead = [
        {
            key: 'numCarteirinha',
            selector: (row) => row.numCarteirinha || '-',
            name: 'Nº da carteirinha',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ paddingTop: '16px', paddingBottom: '16px' }),
        },
        {
            key: 'numCpf',
            selector: (row) => row.numCpf || '-',
            name: 'CPF',
            sortable: true,
            direction: 'left',
            customStyles: () => ({}),
        },
        {
            key: 'nome',
            selector: (row) => row.nome || '-',
            name: 'Nome completo',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ minWidth: '148px', whiteSpace: 'nowrap' }),
        },
        {
            key: 'dtOperacao',
            selector: (row) => row.dtOperacao,
            name: 'Data operação',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ whiteSpace: 'nowrap' }),
        },
        {
            key: 'tipoOperacao',
            selector: (row) => row.tipoOperacao.nome,
            name: 'Tipo de operação',
            sortable: true,
            direction: 'center',
            customStyles: () => ({ whiteSpace: 'nowrap' }),
        },
        {
            key: 'status',
            selector: (row) => row.status,
            name: 'Status',
            sortable: true,
            direction: 'left',
            customStyles: () => ({}),
            mutation: (status) => <CustomStatusTag status={status} />,
        },
        // {
        //     key: 'protocolo',
        //     selector: (row) => row.protocolo !== 0 ? row.protocolo : '-',
        //     name: 'Protocolo',
        //     sortable: true,
        //     direction: 'left',
        //     customStyles: () => ({ whiteSpace: 'nowrap' }),
        // },
        {
            key: 'actions',
            selector: (row) => row,
            name: '',
            sortable: false,
            direction: 'right',
            customStyles: () => ({ cursor: 'pointer' }),
            mutation: (row) => (
                <Icon
                    color={contrastTheme ? '#FFF333' : '#EC6E0C'}
                    name="plagiarism"
                    onClick={() => handleModal(row)}
                />
            ),
        },
    ];

    useEffect(() => {
        const contractFieldData = { ...filterData.contract, options: listAllContracts };

        setFilterData({ ...filterData, contract: contractFieldData });
    }, []);

    return (
        <Box padding="32px">
            <Accordion title="Pesquisar movimentação" hasCollapse={false}>
                <Box width="100%">
                    <Radio field={filterData?.type} onChange={handleChange} />

                    {filterData.type.value === 'perProtocol' && (
                        <S.CustomContainer display="flex">
                            <Input field={filterData?.protocol} onChange={handleChange} />
                        </S.CustomContainer>
                    )}

                    <S.CustomContainer display="flex">
                        {filterData.type.value === 'perBeneficiary' && (
                            <>
                                <Input field={filterData?.identity} onChange={handleChange} />
                                <Select field={filterData?.contract} onChange={handleChange} />
                            </>
                        )}
                        {filterData.type.value === 'perOperation' && (
                            <>
                                <Select field={filterData?.operationType} onChange={handleChange} />
                                <Select field={filterData?.status} onChange={handleChange} />
                            </>
                        )}
                        {filterData.type.value === 'perProtocol' && (
                            <>
                                <Select field={filterData?.operationType} onChange={handleChange} />
                                <Select field={filterData?.status} onChange={handleChange} />
                            </>
                        )}
                        <DatepickerRange
                            field={filterData?.period}
                            disabledDate={(d) => !d || d.isAfter(rangeLimits.max) || d.isSameOrBefore(rangeLimits.min)}
                            onCalendarChange={handleChangeRange}
                            onChange={handleChange}
                        />
                        <S.SearchButton onClick={handleSearch} type="primary">
                            Pesquisar
                        </S.SearchButton>
                    </S.CustomContainer>
                </Box>

                {/* {isAlreadySearch && (
                    <Box width="100%" display="flex" justifyContent="flex-end">
                        <S.CustomButton variant="text">
                            <Icon name="file_download" />

                            <span className="MuiButton-text">Exportar relatório</span>
                        </S.CustomButton>
                    </Box>
                )} */}

                {showTableMessage && <S.TableMessage>Exibindo 100 resultados mais recentes</S.TableMessage>}

                <DataTableMui
                    clickableRow={false}
                    alreadySearched={isAlreadySearch}
                    columns={columnsHead}
                    data={movementHistoryData}
                    emptyMessage="Não encontramos nenhuma movimentação. Você pode fazer uma nova pesquisa."
                    firstOrderBy="nome"
                    loading={isLoading}
                />

                {Object.keys(modalContent).length > 0 && (
                    <S.CustomModal
                        title={modalContent.nome}
                        subtitle={modalContent.numCpf ? `Titular - CPF: ${addMaskCpf(modalContent.numCpf)}` : ''}
                        visible={showModal}
                        onCancel={() => setShowModal(false)}
                        footer={null}
                    >
                        {modalContent.status.codigo === 2 && (
                            <Alert
                                type={1}
                                message="Esta movimentação não foi efetivada. Mais detalhes podem ser observados através da funcionalidade de movimentações pendentes"
                            />
                        )}

                        <div className="type-operation-container">
                            <strong>{modalContent.tipoOperacao.nome}</strong>

                            <p>Solicitação em: {modalContent.dtOperacao}</p>
                            <p>Protocolo: {modalContent.protocolo !== 0 ? modalContent.protocolo : ' -'}</p>
                        </div>

                        {modalContent.history.map((action, index) => (
                            <div key={index} className="action">
                                <b>{action.descricaoOcorrencia}</b>

                                <p>{action.observacao}</p>
                            </div>
                        ))}
                    </S.CustomModal>
                )}
            </Accordion>
        </Box>
    );
}

export default HistoricoDeMovimentacao;
